import React, { useState, useMemo, useEffect, useRef } from "react";
import AudienceBreakdownCard from "../AudienceBreakdownCard";
import QueryBreakdownModal, {
  QueryBreakdownData,
  dpAwareCount,
} from "../QueryBreakdownModal";
import { getLastMonthRange, toDateValue } from "../../utils";
import { useToast } from "../../contexts/toast-context";
import InforIcon from "../../assets/InfoCircleIcon";
import styles from "./audienceOverviewModal.module.css";
import { OI } from "../../onam/mark";
import { FEATURES } from "../../onam/features";

interface CampaignVolumeOption {
  campaign_name: string;
}

interface AudienceOverviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  rows: any[];
  isLoading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onDateChange: (start: string, end: string) => void;
  onLoadMore: () => void;
  // Full campaign universe (from the campaign volumes API) used to populate
  // the search suggestions — independent of how many overview pages have
  // been paginated in, so the filter list isn't missing campaigns whose
  // rows haven't loaded yet.
  campaignOptions?: CampaignVolumeOption[];
}

const CARDS_PER_PAGE = 12;

type SortBy = "date" | "reach-desc" | "reach-asc" | "name";
type PresetKey = "lastMonth" | "month" | "last30";

const MAX_RANGE_MONTHS = 3;
const MIN_SELECTABLE_DATE = "2025-01-01";
const DAY_MS = 24 * 60 * 60 * 1000;

// Local-time parse/format so day math doesn't drift across timezones
// (new Date("YYYY-MM-DD") parses as UTC and can shift by a day locally).
const parseDateValue = (value: string): Date => {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addMonths = (value: string, months: number): string => {
  const d = parseDateValue(value);
  // Clamp to the target month's last day instead of letting Date roll
  // over into the following month (e.g. Dec 31 - 3 months would otherwise
  // land on Oct 1 instead of Sep 30, since September only has 30 days).
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1);
  const lastDayOfTarget = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0,
  ).getDate();
  target.setDate(Math.min(d.getDate(), lastDayOfTarget));
  return toDateValue(target);
};
// MIN_SELECTABLE_DATE is a zero-padded YYYY-MM-DD, so plain string
// comparison sorts the same as chronological order.
const clampToMinDate = (value: string): string =>
  value < MIN_SELECTABLE_DATE ? MIN_SELECTABLE_DATE : value;

// Anything unparseable (including a stray partial year the browser
// zero-padded, e.g. typing "2" into the year segment and blurring produces
// "0002-06-15") or earlier than the selectable window counts as invalid.
// Previously an out-of-range-but-well-formed year (e.g. 2019) slipped past
// this check, fell through to the normal clamp-to-min path below, and
// silently fired a fetch for the clamped range (2025-01-01 through
// MAX_RANGE_MONTHS later) — a range the user never asked for, which could
// hang with no response. Treating anything before MIN_SELECTABLE_DATE as
// invalid up front means that clamped fetch is never made.
const isInvalidDateValue = (value: string): boolean => {
  if (!value) return true;
  if (Number.isNaN(parseDateValue(value).getTime())) return true;
  return value < MIN_SELECTABLE_DATE;
};

interface CampaignGroup {
  key: string;
  latestCard: QueryBreakdownData;
  latestCreatedAt?: string;
  previousRuns: Array<{ data: QueryBreakdownData; createdAt?: string }>;
}

const AudienceOverviewModal: React.FC<AudienceOverviewModalProps> = ({
  isOpen,
  onClose,
  rows,
  isLoading,
  hasMore,
  isLoadingMore,
  onDateChange,
  onLoadMore,
  campaignOptions = [],
}) => {
  const defaultRange = getLastMonthRange();
  const { showToast } = useToast();
  // Upper bound for both date inputs, so the calendar can always be browsed
  // up to the current year regardless of what the other field is set to.
  const todayValue = toDateValue(new Date());

  const [startDate, setStartDate] = useState(toDateValue(defaultRange.start));
  const [endDate, setEndDate] = useState(toDateValue(defaultRange.end));
  // Mirrors startDate/endDate synchronously (state updates land a render
  // later) so commitRange always reads the value just written by onChange,
  // even if a blur fires before that render happens.
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  const committedRangeRef = useRef({ start: startDate, end: endDate });
  const [searchText, setSearchText] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [sortBy, setSortBy] = useState<SortBy>("date");
  const [cardPage, setCardPage] = useState(0);
  const [activePreset, setActivePreset] = useState<PresetKey | null>("lastMonth");
  const [expandedQuery, setExpandedQuery] =
    useState<QueryBreakdownData | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const setStart = (v: string) => {
    startDateRef.current = v;
    setStartDate(v);
  };
  const setEnd = (v: string) => {
    endDateRef.current = v;
    setEndDate(v);
  };
  // Fires the actual data fetch. Called on blur (not on every keystroke/
  // arrow-key tick) so adjusting the picker doesn't refetch until the user
  // is done — mirrors clicking a preset, which already fetches immediately.
  const commitRange = () => {
    const start = startDateRef.current;
    const end = endDateRef.current;
    if (
      committedRangeRef.current.start === start &&
      committedRangeRef.current.end === end
    )
      return;
    committedRangeRef.current = { start, end };
    onDateChange(start, end);
  };

  // Clamping/pairing (min date, max range, no inverted range) is resolved
  // here on blur rather than in onChange. Doing it per-keystroke previously
  // fought the native picker: while a value is only partially typed (e.g.
  // the year segment has 1-3 digits so far), the input's live value briefly
  // reports something like "0002-06-15", and if onChange applied clamping
  // to that value and skipped a state update to avoid stomping it, React
  // state and the DOM's live value diverged — any unrelated re-render
  // elsewhere in the app (a data fetch resolving, a timer tick, etc.) would
  // then force React to reassert the stale committed value onto the input,
  // wiping out whatever the user had typed so far. Keeping state in exact
  // sync with the raw value on every keystroke (see the input onChange
  // handlers) avoids that divergence, and finalizing here means a
  // resync from an unrelated render is always a no-op.
  const finalizeStart = () => {
    if (
      startDateRef.current &&
      isInvalidDateValue(startDateRef.current)
    ) {
      resetToLast30ForInvalidDate();
      return;
    }
    let start = startDateRef.current || committedRangeRef.current.start;
    start = clampToMinDate(start);
    let end = endDateRef.current || committedRangeRef.current.end;
    const maxEnd = addMonths(start, MAX_RANGE_MONTHS);
    if (parseDateValue(end).getTime() > parseDateValue(maxEnd).getTime()) {
      end = maxEnd;
    } else if (parseDateValue(end).getTime() < parseDateValue(start).getTime()) {
      end = start;
    }
    setStart(start);
    setEnd(end);
    commitRange();
  };
  const finalizeEnd = () => {
    if (endDateRef.current && isInvalidDateValue(endDateRef.current)) {
      resetToLast30ForInvalidDate();
      return;
    }
    let end = endDateRef.current || committedRangeRef.current.end;
    let start = startDateRef.current || committedRangeRef.current.start;
    const minStart = clampToMinDate(addMonths(end, -MAX_RANGE_MONTHS));
    if (parseDateValue(start).getTime() < parseDateValue(minStart).getTime()) {
      start = minStart;
    } else if (parseDateValue(start).getTime() > parseDateValue(end).getTime()) {
      start = end;
    }
    start = clampToMinDate(start);
    setStart(start);
    setEnd(end);
    commitRange();
  };

  useEffect(() => {
    if (!isOpen) {
      setExpandedQuery(null);
      setSearchText("");
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      setSortBy("date");
      setCardPage(0);
      // Reset the date range to "last month" so the picker always matches
      // the default range the parent fetches with on next open, instead of
      // carrying over whatever range was left selected last time.
      const range = getLastMonthRange();
      const sStr = toDateValue(range.start);
      const eStr = toDateValue(range.end);
      setStart(sStr);
      setEnd(eStr);
      committedRangeRef.current = { start: sStr, end: eStr };
      setActivePreset("lastMonth");
    }
  }, [isOpen]);

  useEffect(() => {
    setCardPage(0);
  }, [searchText, sortBy]);

  // Reset to first page when rows are cleared (fresh date range fetch)
  useEffect(() => {
    if (rows.length === 0) setCardPage(0);
  }, [rows.length]);

  const getPresetRange = (preset: PresetKey): { start: Date; end: Date } => {
    const n = new Date();
    if (preset === "lastMonth") return getLastMonthRange();
    if (preset === "month")
      return { start: new Date(n.getFullYear(), n.getMonth(), 1), end: n };
    return { start: new Date(n.getTime() - 30 * DAY_MS), end: n };
  };

  const applyPreset = (preset: PresetKey) => {
    const { start: s, end: e } = getPresetRange(preset);
    const sStr = toDateValue(s);
    const eStr = toDateValue(e);
    setStart(sStr);
    setEnd(eStr);
    setActivePreset(preset);
    committedRangeRef.current = { start: sStr, end: eStr };
    onDateChange(sStr, eStr);
  };

  // Shared fallback for both date fields: an invalid year (see
  // isInvalidDateValue) can't be repaired by clamping, so abandon whatever
  // was typed and reset the whole range to last 30 days instead.
  const resetToLast30ForInvalidDate = () => {
    showToast(
      "You've entered an invalid date. Showing the last 30 days instead.",
      "error",
    );
    applyPreset("last30");
  };

  const { groups, totalReach, totalCampaigns } = useMemo(() => {
    const qualified = rows.filter(
      (row) =>
        row.status === "SUCCEEDED" &&
        !row.compound_query_input &&
        (row.differential_privacy_count_total || 0) > 0 &&
        row.executed_queries?.length,
    );

    // Group by campaign_name (null names each form their own group by id)
    const groupMap = new Map<string, CampaignGroup>();
    qualified.forEach((row) => {
      const card: QueryBreakdownData = {
        queryName: row.campaign_name || row.id,
        queryId: row.id,
        campaignName: row.campaign_name,
        intersectionCount: row.differential_privacy_count_total,
        executedQueries: row.executed_queries,
        attributesSelected: row.attributes_selected || [],
        nlQuery: row.nl_query,
        nlQueryText: row.nl_query_params?.input_text,
      };
      const key = row.campaign_name || row.id;
      const existing = groupMap.get(key);
      if (!existing) {
        groupMap.set(key, {
          key,
          latestCard: card,
          latestCreatedAt: row.created_at,
          previousRuns: [],
        });
      } else {
        existing.previousRuns.push({ data: card, createdAt: row.created_at });
      }
    });

    const allGroups = Array.from(groupMap.values());

    const totalReach = allGroups.reduce(
      (s, g) => s + g.latestCard.intersectionCount,
      0,
    );

    // Search filter
    const filtered = searchText.trim()
      ? allGroups.filter((g) =>
          (g.latestCard.campaignName || g.latestCard.queryName)
            .toLowerCase()
            .includes(searchText.toLowerCase()),
        )
      : allGroups;

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "reach-desc")
        return b.latestCard.intersectionCount - a.latestCard.intersectionCount;
      if (sortBy === "reach-asc")
        return a.latestCard.intersectionCount - b.latestCard.intersectionCount;
      if (sortBy === "name")
        return (
          a.latestCard.campaignName || a.latestCard.queryName
        ).localeCompare(
          b.latestCard.campaignName || b.latestCard.queryName,
        );
      return 0; // "date": API already returns newest-first
    });

    return {
      groups: sorted,
      totalReach,
      totalCampaigns: allGroups.length,
    };
  }, [rows, searchText, sortBy]);

  // Full campaign name universe for search suggestions — sourced from the
  // campaign volumes API rather than `rows`, so it's complete regardless of
  // how many overview pages have been paginated in.
  const campaignNameOptions = useMemo(() => {
    const names = new Set<string>();
    campaignOptions.forEach((c) => {
      if (c.campaign_name) names.add(c.campaign_name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [campaignOptions]);

  // Suggestions shown in the custom dropdown below the search input —
  // a plain in-app list (rather than a native <datalist>) so its size,
  // containment within the modal, and scrollbar can be styled directly.
  const filteredSuggestions = useMemo(() => {
    const trimmed = searchText.trim().toLowerCase();
    const pool = trimmed
      ? campaignNameOptions.filter((name) =>
          name.toLowerCase().includes(trimmed),
        )
      : campaignNameOptions;
    return pool.slice(0, 50);
  }, [campaignNameOptions, searchText]);

  const selectSuggestion = (name: string) => {
    setSearchText(name);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  // Shared scale across all cards so bar heights reflect relative audience
  // size instead of each card auto-scaling to its own max (which made every
  // mini chart look about the same height regardless of actual counts).
  const globalMaxCount = useMemo(() => {
    return groups.reduce((max, g) => {
      const localMax = Math.max(
        ...g.latestCard.executedQueries.map((q) => dpAwareCount(q)),
        g.latestCard.intersectionCount,
      );
      return Math.max(max, localMax);
    }, 1);
  }, [groups]);

  // Auto-fetch more API data when the current card page needs more cards than are loaded
  useEffect(() => {
    const needed = (cardPage + 1) * CARDS_PER_PAGE;
    if (!isLoading && !isLoadingMore && hasMore && groups.length < needed) {
      onLoadMore();
    }
  }, [cardPage, groups.length, hasMore, isLoading, isLoadingMore, onLoadMore]);

  // Snap back to the real last page if we advanced past it expecting more
  // data (hasMore was true) but the fetch came back empty — otherwise the
  // user lands on a blank page that still claims to be a valid one.
  useEffect(() => {
    if (isLoading || isLoadingMore || hasMore) return;
    const maxPage = Math.max(0, Math.ceil(groups.length / CARDS_PER_PAGE) - 1);
    if (cardPage > maxPage) {
      setCardPage(maxPage);
      showToast("No more campaigns to display.", "info");
    }
  }, [cardPage, groups.length, hasMore, isLoading, isLoadingMore, showToast]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Audience Overview"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className={styles.header}>
          <div className={styles.headerSpacer} />
          <h2 className={styles.headerTitle}>Audience Overview</h2>
          <div className={styles.headerActions}>
            <button
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              {(FEATURES.curationSkin ? <OI n="close" size="1em" /> : "✕")}
            </button>
          </div>
        </div>

        {/* ── Sub-header ─────────────────────────────────────────── */}
        <div className={styles.subHeader}>
          {/* Row 1: date presets + date pickers */}
          <div className={styles.subHeaderRow}>
            <div className={styles.presets}>
              {(["lastMonth", "month", "last30"] as const).map((p) => (
                <button
                  key={p}
                  className={`${styles.presetBtn}${activePreset === p ? ` ${styles.presetActive}` : ""}`}
                  aria-pressed={activePreset === p}
                  onClick={() => applyPreset(p)}
                >
                  {p === "lastMonth"
                    ? "Last month"
                    : p === "month"
                      ? "This month"
                      : "Last 30d"}
                </button>
              ))}
            </div>
            <div className={styles.dateRange}>
              <label className={styles.dateLabel}>
                From
                <span
                  className={styles.tipWrap}
                  data-tip={`Maximum date range is ${MAX_RANGE_MONTHS} months, from ${MIN_SELECTABLE_DATE.slice(0, 4)} onward`}
                >
                  <InforIcon className={styles.tipIcon} />
                </span>
              </label>
              <input
                type="date"
                className={styles.dateInput}
                value={startDate}
                onChange={(e) => {
                  // Mirror the native value exactly, with no clamping —
                  // clamping here would fight the browser's own mid-typing
                  // state (see the note on finalizeStart above). Range
                  // validation happens once the user is done, on blur.
                  setStart(e.target.value);
                  setActivePreset(null);
                }}
                onBlur={finalizeStart}
                // Bounded by the fixed selectable universe, not by the
                // sibling field's current value — tying max/min to endDate
                // shrank the calendar's browsable range to a 3-month window
                // around whatever "To" happened to be, which could hide the
                // current year entirely (e.g. "Last month" in January caps
                // both bounds within the prior year). The 3-month max-range
                // and start<=end invariants are still enforced on blur by
                // finalizeStart/finalizeEnd regardless of these bounds.
                max={todayValue}
                min={MIN_SELECTABLE_DATE}
              />
              <span className={styles.dateSep}>—</span>
              <label className={styles.dateLabel}>To</label>
              <input
                type="date"
                className={styles.dateInput}
                value={endDate}
                onChange={(e) => {
                  setEnd(e.target.value);
                  setActivePreset(null);
                }}
                onBlur={finalizeEnd}
                min={MIN_SELECTABLE_DATE}
                max={todayValue}
              />
            </div>
          </div>

          {/* Row 2: search + sort */}
          <div className={styles.subHeaderRow}>
            <div className={styles.searchWrap}>
              <input
                ref={searchInputRef}
                type="search"
                className={styles.searchInput}
                placeholder="Search campaigns…"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setShowSuggestions(true);
                  setHighlightedIndex(-1);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  // Delayed so a click on a suggestion (which fires
                  // mousedown → blur before its own click) still lands.
                  window.setTimeout(() => setShowSuggestions(false), 120);
                }}
                onKeyDown={(e) => {
                  if (!showSuggestions || filteredSuggestions.length === 0)
                    return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlightedIndex(
                      (i) => (i + 1) % filteredSuggestions.length,
                    );
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightedIndex(
                      (i) =>
                        (i - 1 + filteredSuggestions.length) %
                        filteredSuggestions.length,
                    );
                  } else if (e.key === "Enter" && highlightedIndex >= 0) {
                    e.preventDefault();
                    selectSuggestion(filteredSuggestions[highlightedIndex]);
                  } else if (e.key === "Escape") {
                    setShowSuggestions(false);
                  }
                }}
              />
              {showSuggestions && filteredSuggestions.length > 0 && (
                <ul className={styles.suggestionList} role="listbox">
                  {filteredSuggestions.map((name, i) => (
                    <li
                      key={name}
                      role="option"
                      aria-selected={i === highlightedIndex}
                      className={`${styles.suggestionItem}${
                        i === highlightedIndex
                          ? ` ${styles.suggestionItemActive}`
                          : ""
                      }`}
                      // Keeps the input focused so onBlur doesn't close the
                      // list before the click below can select from it.
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHighlightedIndex(i)}
                      onClick={() => selectSuggestion(name)}
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <select
              className={styles.sortSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
            >
              <option value="date">Newest first</option>
              <option value="reach-desc">Highest reach</option>
              <option value="reach-asc">Lowest reach</option>
              <option value="name">Campaign A–Z</option>
            </select>
          </div>
        </div>

        {/* ── Summary strip ──────────────────────────────────────── */}
        {!isLoading && totalCampaigns > 0 && (
          <div className={styles.summaryStrip}>
            <div className={styles.summaryMetric}>
              <span className={styles.summaryValue}>
                {hasMore && "~"}{totalReach.toLocaleString("en-US")}
              </span>
              <span className={styles.summaryLabel}>
                Total Matched
                <span
                  className={styles.tipWrap}
                  data-tip="Sum of the latest audience size across all campaigns in this date range. Prefixed with ~ when more data is still loading."
                >
                  <InforIcon className={styles.tipIcon} />
                </span>
              </span>
            </div>
            <div className={styles.summaryDivider} />
            <div className={styles.summaryMetric}>
              <span className={styles.summaryValue}>
                {hasMore && "~"}{totalCampaigns}
              </span>
              <span className={styles.summaryLabel}>
                Campaigns
                <span
                  className={styles.tipWrap}
                  data-tip="Number of unique campaigns with at least one successful audience run in this date range."
                >
                  <InforIcon className={styles.tipIcon} />
                </span>
              </span>
            </div>
          </div>
        )}

        {/* ── Body ───────────────────────────────────────────────── */}
        <div className={styles.body}>
          {isLoading ? (
            <div className={styles.grid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : groups.length === 0 && isLoadingMore ? (
            // More history is still being fetched to look for search matches —
            // show a loading state instead of a false "no results" message.
            <div className={styles.grid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>∅</span>
              <p className={styles.emptyMsg}>
                {searchText
                  ? "No campaigns match your search"
                  : "No audience breakdowns in this date range"}
              </p>
              <p className={styles.emptySub}>
                {searchText
                  ? "Try a different search term"
                  : "Try widening the date range, or run more queries with multiple contributors"}
              </p>
            </div>
          ) : (() => {
            const totalCardPages = Math.ceil(groups.length / CARDS_PER_PAGE);
            // Because groups are deduped campaigns from raw (undeduped) API
            // rows, a page's worth of groups isn't guaranteed to exist yet
            // even when more rows are still fetchable — each fetch can add
            // anywhere from zero to many new groups. Treat the page as
            // still loading (rather than rendering it with a card/total
            // count that's about to change) until it's actually full or
            // there's nothing left to fetch.
            const pageIsLoading =
              hasMore && groups.length < (cardPage + 1) * CARDS_PER_PAGE;
            const pagedGroups = pageIsLoading
              ? []
              : groups.slice(
                  cardPage * CARDS_PER_PAGE,
                  (cardPage + 1) * CARDS_PER_PAGE,
                );
            const isLastPage = cardPage >= totalCardPages - 1;
            return (
              <>
                <div className={styles.grid}>
                  {pageIsLoading
                    ? Array.from({ length: CARDS_PER_PAGE }).map((_, i) => (
                        <SkeletonCard key={i} />
                      ))
                    : pagedGroups.map((group) => (
                        <AudienceBreakdownCard
                          key={group.key}
                          data={group.latestCard}
                          createdAt={group.latestCreatedAt}
                          onExpand={() => setExpandedQuery(group.latestCard)}
                          previousRuns={group.previousRuns}
                          onExpandRun={(data) => setExpandedQuery(data)}
                          globalMaxCount={globalMaxCount}
                        />
                      ))}
                </div>

                {/* Pagination controls */}
                {(totalCardPages > 1 || hasMore) && (
                  <div className={styles.pagination}>
                    <button
                      className={styles.pageBtn}
                      onClick={() => setCardPage((p) => p - 1)}
                      disabled={cardPage === 0 || pageIsLoading}
                    >
                      ← Prev
                    </button>
                    <span className={styles.pageInfo}>
                      {pageIsLoading
                        ? "Loading…"
                        : `${cardPage + 1} / ${hasMore ? `${totalCardPages}+` : totalCardPages}`}
                    </span>
                    <button
                      className={styles.pageBtn}
                      onClick={() =>
                        setCardPage((p) =>
                          p < totalCardPages - 1 || hasMore ? p + 1 : p,
                        )
                      }
                      disabled={(isLastPage && !hasMore) || pageIsLoading}
                    >
                      Next →
                    </button>
                  </div>
                )}

                {/* Auto-fetch indicator — shown while fetching to fill the current page */}
                {pageIsLoading && isLoadingMore && (
                  <div className={styles.autoLoadWrap}>
                    <span className={styles.loadMoreSpinner} />
                    <span className={styles.autoLoadText}>Loading more…</span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Detail modal — DOM order ensures it paints on top at equal z-index */}
      {expandedQuery && (
        <QueryBreakdownModal
          isOpen={true}
          onClose={() => setExpandedQuery(null)}
          data={expandedQuery}
        />
      )}
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className={styles.skeletonCard}>
    <div className={styles.skeletonChart} />
    <div className={styles.skeletonFooter}>
      <div className={styles.skeletonLine} />
      <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
    </div>
  </div>
);

export default AudienceOverviewModal;
